import { useState } from "react";
import {
  createManufacturingVendor,
  type MfgVendor,
} from "@/lib/manufacturing/purchaseOrderClient";
import {
  matchSpokenLinesToCatalog,
  matchVendorName,
  parseSpokenPurchaseOrder,
  type CatalogMatch,
} from "@/lib/manufacturing/spokenCatalogMatch";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { newDraftLine, type PoDraftLine } from "./purchaseOrderDraftState";
import { ManufacturingDictatePad } from "./ManufacturingDictatePad";
import { SpokenCatalogMatchList } from "./SpokenCatalogMatchList";

export function GeneratePurchaseOrderDictate(props: {
  enabled: boolean;
  busy: boolean;
  materials: MfgMaterial[];
  vendors: MfgVendor[];
  plantId: string;
  onVendorsChanged: (vendors: MfgVendor[]) => void;
  onApply: (next: {
    vendorId?: string;
    unmatchedVendorName?: string;
    lines?: PoDraftLine[];
  }) => void;
}) {
  const [unmatchedVendor, setUnmatchedVendor] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [heard, setHeard] = useState<CatalogMatch[]>([]);
  const [parseHint, setParseHint] = useState<string | null>(null);
  const [padReset, setPadReset] = useState(0);

  function handleExtract(transcript: string) {
    const parsed = parseSpokenPurchaseOrder(transcript);
    const catalog = props.materials
      .filter((m) => !props.plantId || m.plant_id === props.plantId)
      .map((m) => ({ id: m.id, name: m.name, code: m.code }));
    const matches = matchSpokenLinesToCatalog(parsed.lines, catalog);
    setHeard(matches);
    const lines: PoDraftLine[] = matches.map((m) => ({
      ...newDraftLine(),
      material_id: m.candidate?.id ?? null,
      material_name: m.candidate?.name ?? m.spoken.name,
      quantity: String(m.spoken.qty),
    }));
    let vendorId = "";
    let unmatchedVendorName = "";
    if (parsed.vendorName) {
      const vMatch = matchVendorName(
        parsed.vendorName,
        props.vendors.map((v) => ({ id: v.id, name: v.name })),
      );
      if (vMatch.candidate && vMatch.status !== "unmatched") {
        vendorId = vMatch.candidate.id;
      } else {
        unmatchedVendorName = parsed.vendorName;
      }
    }
    setUnmatchedVendor(unmatchedVendorName);
    setParseHint(
      parsed.lines.length === 0
        ? "Couldn't find a quantity and material name. Try “quantity 40 item 2x4x96 PT”."
        : null,
    );
    setNote(null);
    props.onApply({ vendorId, unmatchedVendorName, lines: lines.length ? lines : [newDraftLine()] });
    if (parsed.lines.length > 0) setPadReset((n) => n + 1);
  }

  async function createVendor() {
    const name = unmatchedVendor.trim();
    if (!name) return;
    const res = await createManufacturingVendor({ name });
    if (!res.ok) {
      setNote(res.error);
      return;
    }
    props.onVendorsChanged([...props.vendors, res.data.vendor]);
    props.onApply({ vendorId: res.data.vendor.id, unmatchedVendorName: "" });
    setUnmatchedVendor("");
    setNote(`Created vendor ${res.data.vendor.name}.`);
  }

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 dark:border-violet-900 dark:bg-violet-950/20">
      <div className="text-xs font-semibold text-gray-800 dark:text-zinc-100">Dictate PO</div>
      <p className="mb-2 text-[11px] text-gray-500">
        “Order from ABC Lumber: quantity 40 item two by fours…” Draft stays on this device until
        you save.
      </p>
      <ManufacturingDictatePad
        disabled={!props.enabled || props.busy}
        placeholder="Order from ABC Lumber: quantity 40 item 2x4s…"
        extractLabel="Match vendor & materials"
        resetKey={padReset}
        onComplete={handleExtract}
      />
      <SpokenCatalogMatchList matches={heard} emptyHint={parseHint} />
      {note ? (
        <p className="mt-2 text-[11px] text-gray-600 dark:text-zinc-300">{note}</p>
      ) : null}
      {unmatchedVendor ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
          <span>No vendor named “{unmatchedVendor}”.</span>
          <button type="button" className={ghostBtnClass} onClick={() => void createVendor()}>
            Create vendor
          </button>
        </div>
      ) : null}
    </div>
  );
}
